import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { apiFetch, fetchMedecinsMaitre, readApiError } from "./apiMedecins.js";

// Page responsable : gestion des listes de médecins
// - Liste maître (service complet) en lecture seule via API v2
// - Liste Gardes modifiable via API v2 / SQLite ; liste OPH retiree.

function sanitizeBackParam(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default function OngletListesMedecins() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [master, setMaster] = useState([]);
  const [gardes, setGardes] = useState([]);

  const [savingGardes, setSavingGardes] = useState(false);
  const [info, setInfo] = useState(null);

  const keyFor = (m) => (m?.medecin_id ? String(m.medecin_id) : `${m?.nom || ""}|${m?.prenom || ""}`).trim();

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setInfo(null);

      const [masterData, gardesRes] = await Promise.all([
        fetchMedecinsMaitre(),
        apiFetch("/api/v2/medecins-gardes-pr2", { cache: "no-store" }),
      ]);

      const ensureOk = (res, label) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} sur ${label}`);
      };

      ensureOk(gardesRes, "/api/v2/medecins-gardes-pr2");

      const gardesData = (await gardesRes.json()) || [];

      setMaster(masterData);
      setGardes(gardesData);
    } catch (e) {
      console.error("❌ Chargement listes médecins :", e);
      setError(e.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sets = useMemo(() => {
    const base = new Map();
    master.forEach((m) => base.set(keyFor(m), m));

    // Garder trace des entrées hors liste maître
    const gardesOnly = [];
    gardes.forEach((m) => {
      const k = keyFor(m);
      if (!base.has(k)) gardesOnly.push(m);
    });

    const gardesKeys = new Set(gardes.map(keyFor));

    // Union pour affichage
    const rows = Array.from(base.values()).map((m) => ({
      ...m,
      inGardes: gardesKeys.has(keyFor(m)),
      isExtra: false,
    }));

    // Ajouter les entrées hors maître pour ne pas les perdre
    gardesOnly.forEach((m) => {
      rows.push({ ...m, inGardes: true, isExtra: true });
    });

    rows.sort((a, b) => {
      const cmpNom = (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" });
      if (cmpNom !== 0) return cmpNom;
      return (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" });
    });

    return { rows, gardesKeys, base };
  }, [master, gardes]);

  const toggleInList = (target, med) => {
    const key = keyFor(med);
    const sourceMap = sets.base;
    const record =
      sourceMap.get(key) ||
      gardes.find((m) => keyFor(m) === key) ||
      med;

    if (target === "gardes") {
      const exists = gardes.find((m) => keyFor(m) === key);
      if (exists) {
        setGardes((prev) => prev.filter((m) => keyFor(m) !== key));
      } else {
        setGardes((prev) => [...prev, record]);
      }
    }
  };

  const resetFromMaster = (target) => {
    if (target === "gardes") setGardes(master);
    setInfo('Liste Gardes réinitialisée depuis la liste maître (non sauvegardée).');
  };

  const saveList = async (target) => {
    try {
      if (target === "gardes") setSavingGardes(true);
      setInfo(null);
      setError(null);

      const list = gardes;
      const url = "/api/v2/medecins-gardes-pr2";
      const res = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(list),
      });
      if (!res.ok) {
        throw new Error(await readApiError(res, `HTTP ${res.status} sauvegarde ${target}`));
      }
      const savedPayload = await res.json().catch(() => null);
      if (Array.isArray(savedPayload?.list)) {
        if (target === "gardes") setGardes(savedPayload.list);
      }
      const countAffiché = new Set(list.map(keyFor)).size;
      setInfo(`Liste Gardes sauvegardée (${countAffiché} médecins).`);
    } catch (e) {
      console.error("❌ Sauvegarde liste médecins :", e);
      setError(e.message || "Erreur inconnue");
    } finally {
      setSavingGardes(false);
    }
  };

  const resolvedBackPath = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const rawBack = sanitizeBackParam(params.get("back"));
    if (rawBack) {
      try {
        const url = new URL(rawBack, window.location.origin);
        const path = `${url.pathname}${url.search || ""}${url.hash || ""}`;
        if (url.pathname === "/portail-medecin" || url.pathname === "/portail-medecin/") return path;
        if (url.pathname.includes("/superviseur")) return path;
        if (url.pathname === "/responsable" || url.pathname === "/responsable/") {
          const nestedBack = sanitizeBackParam(url.searchParams.get("back"));
          const nestedUrl = nestedBack ? new URL(nestedBack, window.location.origin) : null;
          if (
            nestedUrl &&
            (nestedUrl.pathname === "/portail-medecin" ||
              nestedUrl.pathname === "/portail-medecin/" ||
              nestedUrl.pathname.includes("/superviseur"))
          ) {
            return path;
          }
        }
      } catch {
        // Ignore les valeurs non parseables et conserve le fallback supervision.
      }
    }
    return "/superviseur/index.html";
  }, [location.search]);

  const handleBack = () => {
    window.location.href = resolvedBackPath;
  };

  const S = {
    container: { maxWidth: 1100, margin: "40px auto", padding: "20px" },
    title: { fontSize: 34, fontWeight: 800, marginBottom: 20 },
    cards: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 },
    card: {
      padding: 16,
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      background: "#fff",
      boxShadow: "0 6px 14px rgba(0,0,0,0.04)",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
    th: { textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: "10px 8px" },
    td: { borderBottom: "1px solid #f3f4f6", padding: "8px" },
    pill: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#e5e7eb",
    },
    extra: { color: "#b91c1c", fontSize: 12, fontWeight: 700 },
    btnRow: { display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" },
    btn: {
      padding: "10px 14px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      background: "#111827",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 700,
    },
    btnGhost: {
      padding: "10px 14px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      cursor: "pointer",
      fontWeight: 700,
    },
    headerActions: { display: "flex", alignItems: "center", gap: 10 },
  };

  if (loading) {
    return (
      <div style={S.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
          <h1 style={S.title}>Listes des médecins</h1>
          <div style={S.headerActions}>
            <button style={S.btnGhost} onClick={handleBack}>
              ← Retour à la page précédente
            </button>
            <LogoutButton />
          </div>
        </div>
        <p>Chargement…</p>
      </div>
    );
  }

  return (
    <div style={S.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
        <h1 style={S.title}>Listes des médecins</h1>
        <div style={S.headerActions}>
          <button style={S.btnGhost} onClick={handleBack}>
            ← Retour à la page précédente
          </button>
          <LogoutButton />
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, color: "#b91c1c", fontWeight: 700 }}>
          ⚠️ {error}
        </div>
      )}
      {info && (
        <div style={{ marginBottom: 12, color: "#065f46", fontWeight: 700 }}>
          ✅ {info}
        </div>
      )}

      <div style={S.cards}>
        <div style={S.card}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Liste maître (service)</h3>
          <p style={{ margin: "6px 0 0 0", color: "#6b7280" }}>
            Lecture seule
          </p>
          <p style={{ margin: "8px 0 0 0", fontWeight: 700 }}>{master.length} médecins</p>
        </div>


        <div style={S.card}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Gardes</h3>
          <p style={{ margin: "6px 0 0 0", color: "#6b7280" }}>
            Modifiable
          </p>
          <p style={{ margin: "8px 0 0 0", fontWeight: 700 }}>{sets.gardesKeys.size} médecins sélectionnés</p>
          <div style={S.btnRow}>
            <button style={S.btn} onClick={() => saveList("gardes")} disabled={savingGardes}>
              {savingGardes ? "Sauvegarde…" : "Enregistrer Gardes"}
            </button>
            <button style={S.btnGhost} onClick={() => resetFromMaster("gardes")} disabled={savingGardes}>
              Réinitialiser depuis maître
            </button>
          </div>
        </div>
      </div>

      <div style={S.card}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Affectation par médecin</h3>
        <p style={{ margin: "6px 0 12px 0", color: "#6b7280" }}>
          Cochez pour inclure/exclure un médecin de la liste Gardes. Les entrées “hors liste maître” sont conservées et peuvent être retirées manuellement.
        </p>

        <div style={{ maxHeight: 520, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nom</th>
                <th style={S.th}>Prénom</th>
                <th style={S.th}>Statut</th>
                <th style={S.th}>Gardes</th>
                <th style={S.th}>Infos</th>
              </tr>
            </thead>
            <tbody>
              {sets.rows.map((m) => {
                const k = keyFor(m);
                return (
                  <tr key={k}>
                    <td style={S.td}>{m.nom}</td>
                    <td style={S.td}>{m.prenom}</td>
                    <td style={S.td}>{m.statut || "—"}</td>
                    <td style={S.td}>
                      <input
                        type="checkbox"
                        checked={m.inGardes}
                        onChange={() => toggleInList("gardes", m)}
                      />
                    </td>
                    <td style={S.td}>
                      {m.isExtra ? <span style={S.extra}>Hors liste maître</span> : <span style={S.pill}>Maître</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
